import React, { useState } from "react";
import RootAdmin from "../../../../components/layouts/RootAdmin";
import { Button, Card, CardBody, CardHeader, Divider, Input, Select, SelectItem, TableCell, TableRow } from "@nextui-org/react";
import { useForm } from "react-hook-form";
import useListCompany from "../../../../hooks/useListCompany";
import ControlledReactSelect from "../../../../components/elements/ControlledReactSelect";
import ControlledInput from "../../../../components/elements/ControlledInput";
import { postFetcher } from "../../../../services/api";
import toast from "react-hot-toast";
import ClientTablePagination from "../../../../components/elements/ClientTablePagination";
import * as XLSX from "xlsx";
import TipeSurat from "../../../../enums/tipeSurat";
import { yupResolver } from "@hookform/resolvers/yup";
import { addVehicleTransportReportValidation } from "../../../../services/validation/add-vehicle-transport-report.validation";
import { filterValidRows, isResponseErrorObject } from "../../../../services/helpers";
import { ArrowDownTrayIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";

export default function VehicleAddPage() {
  const fileUrl = "/files/Draft_Form_Tambah_Kendaraan.xlsx";
  const navigate = useNavigate();
  const [vehicleData, setVehicleData] = useState([]);
  const [b3Data, setB3Data] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);
  const { dataCompany, isLoadingCompany } = useListCompany();
  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { isSubmitting, errors },
  } = useForm({ resolver: yupResolver(addVehicleTransportReportValidation) });
  const headerTableVehicle = ["No", "No_Polisi", "Merek", "Tahun", "No_Rangka", "No_Mesin", "Kepemilikan"];
  const formattedHeaderTableVehicle = headerTableVehicle.map((header) => header.replace(/_/g, " "));
  const headerTableMaterial = ["No", "Nama_Dagang", "Cas_Number", "Karakteristik", "Fasa_B3", "Jenis_Kemasan", "Tujuan_Penggunaan"];
  const formattedHeaderTableMaterial = headerTableMaterial.map((header) => header.replace(/_/g, " "));
  const type = watch("tipe");

  const contentVehicle = (item) => (
    <TableRow key={item.id}>
      <TableCell>{item.No}</TableCell>
      <TableCell>{item.No_Polisi}</TableCell>
      <TableCell>{item.Merek}</TableCell>
      <TableCell>{item.Tahun}</TableCell>
      <TableCell>{item.No_Rangka}</TableCell>
      <TableCell>{item.No_Mesin}</TableCell>
      <TableCell>{item.Kepemilikan}</TableCell>
    </TableRow>
  );

  const contentMaterial = (item) => (
    <TableRow key={item.id}>
      <TableCell>{item.No}</TableCell>
      <TableCell>{item.Nama_Dagang}</TableCell>
      <TableCell>{item.Cas_Number}</TableCell>
      <TableCell>{item.Karakteristik}</TableCell>
      <TableCell>{item.Fasa_B3}</TableCell>
      <TableCell>{item.Jenis_Kemasan}</TableCell>
      <TableCell>{item.Tujuan_Penggunaan}</TableCell>
    </TableRow>
  );

  const processBahanB3Data = (b3Data) => {
    const processedData = [];

    b3Data.forEach((b3) => {
      const namaDagang = b3["Nama_Dagang"];
      const casNumberString = b3["Cas_Number"];

      // Split CAS number berdasarkan koma dan extract hanya angka CAS
      const casNumbers = casNumberString
        .split(",")
        .map((casEntry) => {
          // Extract CAS number dari format "Nama / CAS-Number"
          const casMatch = casEntry.trim().match(/(\d{2,7}-\d{2}-\d{1})/);
          return casMatch ? casMatch[1] : null;
        })
        .filter((cas) => cas !== null);

      // Duplikasi nama dagang untuk setiap CAS number
      casNumbers.forEach((casNumber) => {
        processedData.push({
          namaDagang: namaDagang,
          casNumber: casNumber,
          karakteristikB3: b3["Karakteristik"],
          fasaB3: b3["Fasa_B3"],
          jenisKemasan: b3["Jenis_Kemasan"],
          tujuanPenggunaan: b3["Tujuan_Penggunaan"],
        });
      });
    });

    return processedData;
  };

  function handleFileUpload(event) {
    const file = event.target.files[0];
    if (file) {
      setIsLoading(true);
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = e.target.result;
          const workbook = XLSX.read(data, { type: "binary" });

          const vehicleSheet = XLSX.utils.sheet_to_json(workbook.Sheets["Data Kendaraan"], { header: 1 });
          if (vehicleSheet.length > 1) {
            const [...rows] = vehicleSheet;
            const vehicles = filterValidRows(rows, headerTableVehicle);
            vehicles.shift();
            setVehicleData(vehicles);
            console.log(vehicles);
          }

          const b3Sheet = XLSX.utils.sheet_to_json(workbook.Sheets["Data Bahan B3"], { header: 1 });
          if (b3Sheet.length > 1) {
            const [...rows] = b3Sheet;
            const substances = filterValidRows(rows, headerTableMaterial);
            substances.shift();
            setB3Data(substances);
          }
        } catch (error) {
          toast.error("Failed to process the file. Please check the format.");
        } finally {
          setIsLoading(false);
          setIsUploaded(true);
        }
      };
      reader.readAsArrayBuffer(file);
    }
  }

  async function handleOnSubmit(formData) {
    try {
      if (vehicleData.length === 0 || b3Data.length === 0) {
        toast.error("Data kendaraan dan bahan b3 harus ada");
        return;
      }

      const newPayload = {
        tipe: formData.tipe,
        companyId: formData.companyId,
        nomorSuratPerusahaan: formData.nomorSuratPerusahaan,
        nomorSuratRekomendasi: formData.nomorSuratRekomendasi ?? null,
        tanggalSuratPerusahaan: formData.tanggalSuratPerusahaan,
        tanggalSuratRekomendasi: formData.tanggalSuratRekomendasi ?? null,
        vehicles: vehicleData?.map((vehicle) => ({
          noPolisi: vehicle["No_Polisi"],
          modelKendaraan: vehicle["Merek"],
          tahunPembuatan: vehicle["Tahun"],
          nomorRangka: vehicle["No_Rangka"],
          nomorMesin: vehicle["No_Mesin"],
          kepemilikan: vehicle["Kepemilikan"],
        })),
        bahanb3: processBahanB3Data(b3Data),
      };
      await postFetcher("/api/rekom/permohonan/add-manual-surat/from-excel", newPayload);
      toast.success("Kendaraan berhasil ditambah");
      //back
      navigate(-1);
    } catch (error) {
      isResponseErrorObject(error.response.data.message)
        ? Object.entries(error.response.data.message).forEach(([key, value]) => {
            toast.error(value);
          })
        : toast.error(error.response.data.message);
    }
  }

  const handleCancel = () => {
    setVehicleData([]);
    setB3Data([]);
    setIsUploaded(false);
    setValue("file", null);
  };

  return (
    <RootAdmin>
      <Card radius="sm">
        <CardBody>
          <div className="w-full flex items-center  gap-3">
            <div className="flex items-center gap-2">
              <Button onPress={() => navigate(-1)} isDisabled={isSubmitting} color="warning" variant="flat">
                Kembali
              </Button>
              <a href={fileUrl} download="Draft Kendaraan dan Bahan B3.xlsx">
                <Button startContent={<ArrowDownTrayIcon className="size-4" />} color="success">
                  Unduh Format File
                </Button>
              </a>
            </div>
            <Divider orientation="vertical" className="h-10" />
            <div className="text-2xl font-semibold">Tambah Manual Surat Rekomendasi</div>
          </div>
        </CardBody>
      </Card>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-5">
        <Card radius="sm" className="col-span-3 md:col-span-1">
          <CardBody>
            <form onSubmit={handleSubmit(handleOnSubmit)} className="flex flex-col gap-3">
              <Select
                {...register("tipe")}
                variant="faded"
                label="Tipe Surat"
                placeholder="Pilih"
                labelPlacement="outside"
                color={errors.tipe ? "danger" : "default"}
                isInvalid={errors.tipe}
                errorMessage={errors.tipe && errors.tipe.message}
              >
                <SelectItem key={TipeSurat.BARU}>{TipeSurat.BARU}</SelectItem>
                <SelectItem key={TipeSurat.PENAMBAHAN_JENIS_B3}>{TipeSurat.PENAMBAHAN_JENIS_B3}</SelectItem>
                <SelectItem key={TipeSurat.PENAMBAHAN_KENDARAAN}>{TipeSurat.PENAMBAHAN_KENDARAAN}</SelectItem>
                <SelectItem key={TipeSurat.PERPANJANGAN}>{TipeSurat.PERPANJANGAN}</SelectItem>
              </Select>
              <ControlledReactSelect control={control} isLoading={isLoadingCompany} name="companyId" menuPosition="absolute" options={dataCompany?.data?.map((item) => ({ label: item.name, value: item.id }))} label="Perusahaan" />
              {type !== TipeSurat.BARU && (
                <>
                  <ControlledInput control={control} name="nomorSuratRekomendasi" label="Nomor Surat Rekomendasi" isRequired={true} />
                  <ControlledInput control={control} type="date" name="tanggalSuratRekomendasi" label="Tanggal Surat Rekomendasi" isRequired={true} />
                </>
              )}
              <ControlledInput control={control} name="nomorSuratPerusahaan" label="Nomor Surat Perusahaan" isRequired={true} />
              <ControlledInput control={control} type="date" name="tanggalSuratPerusahaan" label="Tanggal Surat Perusahaan" isRequired={true} />
              <div className="flex items-end gap-1">
                <Input
                  {...register("file", { onChange: handleFileUpload })}
                  accept=".xlsx"
                  type="file"
                  label="File"
                  labelPlacement="outside"
                  isRequired
                  variant="faded"
                  color={errors.file ? "danger" : "default"}
                  isInvalid={!!errors.file}
                  errorMessage={errors.file && errors.file?.message}
                />
                {isUploaded && (
                  <Button onClick={handleCancel} color="danger" isIconOnly>
                    <TrashIcon className="size-4" />
                  </Button>
                )}
              </div>
              <p className="text-xs text-warning">Pastikan isi file sudah sesuai dengan file format yang disediakan</p>
              <div className="mt-4">
                <Button type="submit" isLoading={isSubmitting} isDisabled={isSubmitting} color="primary">
                  Submit
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>
        <Card radius="sm" className="col-span-3 md:col-span-2">
          <CardHeader></CardHeader>
          <CardBody className="flex flex-col gap-3">
            <ClientTablePagination header={formattedHeaderTableVehicle} content={contentVehicle} data={vehicleData} isLoading={isLoading} />
            <ClientTablePagination header={formattedHeaderTableMaterial} content={contentMaterial} data={b3Data} isLoading={isLoading} />
          </CardBody>
        </Card>
        <div className="col-span-3 md:col-span-2"></div>
      </div>
    </RootAdmin>
  );
}
